/**
 * PartnerTiersPanel — SHARED marketing surface for PooPartner tiers.
 *
 * Single source of truth for the tier-cards grid + Level Up ladder +
 * payout table. Rendered in TWO places:
 *
 *   1. `/partner/tiers` — via `<PartnerTiersPage>` (dedicated route,
 *      full page with the "PooPartner Tiers — the ladder at a glance"
 *      heading + `Not sure yet? Start free` secondary CTA below).
 *   2. `/pricing` "For PooPartners" tab — via `<Pricing>` (renders the
 *      panel inline under the audience toggle when `view === "partners"`).
 *
 * Canonical visual spec: `/app/memory/poopartner_tiers_final.html`
 * (attached by product). Design decisions extracted into
 * `/app/memory/TIER_PAGE_SPEC.md` — that doc is the audit trail for
 * every color / spacing / copy choice below.
 *
 * Copy invariants (Feb 2026 amendment):
 *   - Payout table header EXACTLY: "What you keep vs going solo".
 *     No trailing "cell-wins" clause.
 *   - No footer line claiming "every-tier scenario beats solo".
 *   - Zero literal "%" characters in user-visible marketing copy —
 *     dollar-keep language only.
 *   - No invented tier badges. The four tiers are visually
 *     distinguished ONLY by ribbons, backgrounds, and payout-ladder
 *     pills as defined in the HTML spec. Explicitly banned badges
 *     (regressions from the old inline `/pricing` grid) are asserted
 *     by the copy-audit tests — the phrases themselves live only in
 *     those tests to keep this source file substring-clean.
 * Enforced at Jest-time by `PartnerTiersPage.copyAudit.test.jsx` +
 * `Pricing.partnerTab.copyAudit.test.jsx`.
 *
 * Props:
 *   - `onTierCta(tierId: string)` — parent handles the CTA click (auth
 *     check, API POST, toast, navigation). The panel stays presentational.
 *   - `busyTierId: string | null` — which card is mid-request; renders
 *     that card's CTA in a loading state.
 *   - `showPayoutTable?: boolean` (default: false) — gate for the
 *     "What you keep vs going solo" comparison table. HIDDEN on public
 *     marketing surfaces (`/partner/tiers`, `/pricing` For PooPartners
 *     tab) because that table exposes derivable margin per tier
 *     (customer-pays vs partner-keeps). Future authenticated partner
 *     signup / fee-disclosure step (P3-P6 backlog: Partner Onboarding)
 *     opts in explicitly by passing `showPayoutTable={true}` — same
 *     component, same copy, gated visibility.
 *
 * The panel does NOT render `<PublicPageShell>` or any outer chrome —
 * each host page decides its own frame. Nor does it render the page
 * heading / subhead — the tier page prints its own H1 above; the
 * pricing tab prints its "Keep more of what you earn." section header
 * (already gated by `view === "partners"`) above.
 */
import React from "react";
import { Loader2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Design tokens — mirrored from the HTML spec `:root` block.
// Kept as inline style constants so the JSX reads 1:1 with the source
// visual and no Tailwind theme extension is needed.
// ---------------------------------------------------------------------------
const T = {
  orange:  "#F89623",
  char:    "#374751",
  grey:    "#EEF1F3",
  purple:  "#574B8C",
  prefBg:  "#F1EFF7",
  eliteBg: "#FEEED8",
  bubble1: "#FFF6E8",
  muted:   "#6B7A85",
  mutedDk: "#B9C4CC",
  dashed:  "#E2E7EB",
};

// ---------------------------------------------------------------------------
// Tier card data — copy VERBATIM from the HTML spec.
// ---------------------------------------------------------------------------
const TIER_CARDS = [
  {
    id: "standard",
    ribbonLabel: "Grinder",
    ribbonBg: T.purple,
    ribbonFg: "#fff",
    cardBg: "#fff",
    cardBorder: `1.5px solid ${T.orange}`,
    tname: "Standard",
    who: "Hustlers | Side-Cash | Startups",
    price: "$0",
    priceUnit: "/mo",
    commLabel: (
      <b style={{ color: T.orange }}>Your keep starts at $16/Pluck</b>
    ),
    commBg: T.grey,
    commFg: T.char,
    perks: [
      { text: "Bookings & job management", dim: false },
      { text: "Calendar & scheduling", dim: false },
      { text: "Fast Stripe payouts", dim: false },
      { text: "Analytics & trends", dim: true },
      { text: "Pluckonomics hub (P&L, break-even)", dim: true },
      { text: "Priority placement", dim: true },
    ],
    // Note: gate text intentionally contains ~13 Plucks/day — the "~"
    // is a tilde, not a %; the copy-audit test's %-ban still passes.
    gate: (
      <>Auto-promotes to Preferred at roughly <b>~13 Plucks/day</b> ($100K/yr pace).</>
    ),
    ctaLabel: "Start free",
    ctaTestId: "tier-cta-standard",
    ctaStyle: {
      background: T.grey,
      color: T.char,
      border: `1.5px solid ${T.orange}`,
    },
  },
  {
    id: "preferred",
    ribbonLabel: "Operator",
    ribbonBg: T.orange,
    ribbonFg: T.char,
    cardBg: T.prefBg,
    cardBorder: `1.5px solid ${T.purple}`,
    tname: "Preferred",
    who: "Solo Routes | Growing Teams",
    price: "$99",
    priceUnit: "/mo",
    commLabel: (
      <b style={{ color: T.purple }}>Keep more on every booking</b>
    ),
    commBg: "#fff",
    commFg: T.char,
    perks: [
      { text: "Everything in Standard", dim: false },
      { text: "Earnings analytics & trends", dim: false },
      { text: "Goal tracking", dim: false },
      { text: "Higher payout on every booking", dim: false },
      { text: "Full Pluckonomics hub", dim: true },
      { text: "Priority placement", dim: true },
    ],
    gate: (
      <>Pays for itself above <b>~2 Plucks/day</b>. At 13+ Plucks/day you can net <b>$500+/mo more</b> than Standard.</>
    ),
    ctaLabel: "Upgrade",
    ctaTestId: "tier-cta-preferred",
    ctaStyle: {
      background: T.purple,
      color: "#fff",
    },
  },
  {
    id: "elite",
    ribbonLabel: "Back Office",
    ribbonBg: T.purple,
    ribbonFg: "#fff",
    cardBg: T.eliteBg,
    cardBorder: `2px solid ${T.orange}`,
    cardShadow: "0 12px 40px rgba(248,150,35,.18)",
    tname: "Elite",
    who: "Multi-Crew | High Volume",
    price: "$549",
    priceUnit: "/mo",
    commLabel: (
      <>Your back office + our highest payout — every job, every time</>
    ),
    commBg: "#fff",
    commFg: T.char,
    perks: [
      { text: "Everything in Preferred", dim: false },
      { text: "Full Pluckonomics hub: P&L & break-even", dim: false },
      { text: "Benchmarks & market comparisons", dim: false },
      { text: "Exports (PDF / Excel)", dim: false },
      {
        text: (
          <>Branded exports — YOUR logo &amp; colors on P&amp;L PDFs, graphs, reports <i style={{ color: T.muted, fontWeight: 600 }}>(coming)</i></>
        ),
        dim: false,
      },
      { text: "Priority placement in search", dim: false },
      { text: "Our highest payout rate", dim: false },
    ],
    gate: (
      <>Beats Preferred above <b>~12 Plucks/day</b>. At <b>125+ Plucks/day</b> ($1M/yr scale) you can net <b>$5,000+/mo more</b> than Preferred.</>
    ),
    ctaLabel: "Get Elite",
    ctaTestId: "tier-cta-elite",
    ctaStyle: {
      background: T.orange,
      color: T.char,
    },
  },
  {
    id: "enterprise",
    ribbonLabel: "Concierge",
    ribbonBg: T.orange,
    ribbonFg: T.char,
    cardBg: T.char,
    cardFg: "#fff",
    cardBorder: `1.5px solid ${T.orange}`,
    seg: "Enterprise & Commercial",
    who: "Multi-Crew | Commercial | Custom Terms",
    price: "Let's build it",
    priceSize: 26,
    priceUnit: "",
    commLabel: (
      <b style={{ color: T.orange }}>Custom pricing &amp; terms</b>
    ),
    commBg: "rgba(255,255,255,.12)",
    commFg: "#fff",
    perks: [
      { text: "Everything in Elite", dim: false },
      { text: "Negotiated rates & volume terms", dim: false },
      { text: "Commercial & HOA contracts", dim: false },
      { text: "Dedicated onboarding", dim: false },
      { text: "Custom branding options (coming)", dim: false },
    ],
    gate: (
      <>For operators who don&apos;t fit a card. Bring us your business — we&apos;ll shape the deal around it.</>
    ),
    ctaLabel: "Talk to us",
    ctaTestId: "tier-cta-enterprise",
    ctaStyle: {
      background: "#fff",
      color: T.char,
    },
    // Enterprise-only dogmark. Source is the canonical /brand/poopluckers-dogmark.png
    // (mostly dark-charcoal Scottie + orange accents). The CSS filter converts the
    // dark portions to spec-orange #F89623 so the whole mark reads as a single
    // orange silhouette on the charcoal card — matching the fresh spec's inline
    // base64 preview (see /app/memory/poopartner_tiers_final.html line 164).
    image: {
      src: "/brand/poopluckers-dogmark.png",
      alt: "PooPluckers dogmark",
      wrapperStyle: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 0",
      },
      imgStyle: {
        width: "70%",
        maxWidth: 220,
        // Filter chain: brightness(0) collapses to pure black; the subsequent
        // sepia/hue-rotate/saturate chain shifts black → #F89623. Verified via
        // https://codepen.io/sosuke/pen/Pjoqqp for orange target.
        filter:
          "brightness(0) saturate(100%) invert(64%) sepia(76%) saturate(2124%) hue-rotate(346deg) brightness(96%) contrast(96%)",
      },
    },
  },
];

// ---------------------------------------------------------------------------
// "Level Up As You Grow" — 3-bubble growth ladder. Copy verbatim.
// ---------------------------------------------------------------------------
const LEVEL_STEPS = [
  {
    num: "<13",
    unit: "Plucks/day",
    slab: "Standard",
    ssub: "Hobbyist volume. Standard payouts, no monthly fee.",
    bubbleBg: T.bubble1,
    bubbleFg: T.char,
    borderColor: T.orange,
    slabColor: T.char,
  },
  {
    num: "13+",
    unit: "Plucks/day",
    slab: "Forced → Preferred",
    ssub: "Notified + 1 billing cycle grace. Payout per booking RISES — the promotion pays them more.",
    bubbleBg: T.orange,
    bubbleFg: "#fff",
    borderColor: T.orange,
    slabColor: T.purple,
  },
  {
    num: "125+",
    unit: "Plucks/day",
    slab: "Forced → Elite",
    ssub: "$1M/yr pace. Highest payout rate + $549/mo. Still +$5K/mo better than Preferred at this volume.",
    bubbleBg: T.char,
    bubbleFg: "#fff",
    borderColor: T.orange,
    slabColor: T.purple,
    ring: `0 0 0 4px rgba(248,150,35,.25)`,
  },
];

// ---------------------------------------------------------------------------
// Payout table — LOCKED copy per Feb 2026 amendment. Rows verbatim
// from the HTML spec's `.mathbar` block.
// ---------------------------------------------------------------------------
const PAYOUT_ROWS = [
  { job: "1 dog weekly",  cust: "$20", standard: ["$16.00", "+$1.00"],  preferred: ["$17.50", "+$2.50"],  elite: ["$18.80", "+$3.80"],  solo: "$15" },
  { job: "2 dogs weekly", cust: "$27", standard: ["$21.60", "+$1.60"],  preferred: ["$23.62", "+$3.62"],  elite: ["$25.38", "+$5.38"],  solo: "$20" },
  { job: "3 dogs weekly", cust: "$34", standard: ["$27.20", "+$2.20"],  preferred: ["$29.75", "+$4.75"],  elite: ["$31.96", "+$6.96"],  solo: "$25" },
  { job: "1 dog one-time",cust: "$80", standard: ["$64.00", "+$4.00"],  preferred: ["$70.00", "+$10.00"], elite: ["$75.20", "+$15.20"], solo: "$60" },
  { job: "2 dogs one-time",cust:"$85", standard: ["$72.00", "+$2.00"],  preferred: ["$78.75", "+$8.75"],  elite: ["$84.60", "+$14.60"], solo: "$70" },
];

// EXACT copy per amendment — do NOT add "cell-wins" or any similar
// trailing clause. Verified by the copyAudit test's exact-string
// assertion.
const PAYOUT_TABLE_HEADER = "What you keep vs going solo";

// ---------------------------------------------------------------------------
// UI helpers — inline styled ribbon (45° corner banner).
// ---------------------------------------------------------------------------
function Ribbon({ label, bg, fg }) {
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 120,
        height: 120,
        overflow: "hidden",
        pointerEvents: "none",
      }}
      aria-hidden
    >
      <div
        style={{
          position: "absolute",
          display: "block",
          width: 170,
          padding: "6px 0",
          background: bg,
          color: fg,
          fontSize: 9.5,
          fontWeight: 800,
          letterSpacing: ".6px",
          textTransform: "uppercase",
          textAlign: "center",
          left: -25,
          top: 44,
          transform: "rotate(45deg)",
          boxShadow: `0 2px 8px ${bg}66`,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TierCard — renders one of the 4 columns.
// ---------------------------------------------------------------------------
function TierCard({ card, onCta, isLoading }) {
  const isDark = card.id === "enterprise";
  const textFg = card.cardFg || T.char;
  return (
    <div
      data-testid={`tier-card-${card.id}`}
      style={{
        background: card.cardBg,
        color: textFg,
        borderRadius: 16,
        padding: "34px 22px 26px",
        border: card.cardBorder,
        boxShadow: card.cardShadow || "none",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        minHeight: 620,
      }}
    >
      <Ribbon label={card.ribbonLabel} bg={card.ribbonBg} fg={card.ribbonFg} />

      {card.seg && (
        <div
          style={{
            color: T.orange,
            marginTop: 14,
            maxWidth: "70%",
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {card.seg}
        </div>
      )}
      {!card.seg && (
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            margin: "6px 0 2px",
            maxWidth: "78%",
            color: textFg,
          }}
          data-testid={`tier-tname-${card.id}`}
        >
          {card.tname}
        </div>
      )}
      <div
        style={{
          fontSize: 12.5,
          color: isDark ? T.mutedDk : T.muted,
          minHeight: 34,
          maxWidth: isDark ? "70%" : "82%",
          marginBottom: isDark ? 10 : 0,
        }}
      >
        {card.who}
      </div>
      <div
        style={{
          margin: isDark ? "22px 0 2px" : "16px 0 2px",
          fontSize: card.priceSize || 36,
          fontWeight: 800,
          letterSpacing: "-1px",
          color: textFg,
        }}
        data-testid={`tier-price-${card.id}`}
      >
        {card.price}
        {card.priceUnit && (
          <small
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: isDark ? T.mutedDk : T.muted,
            }}
          >
            {card.priceUnit}
          </small>
        )}
      </div>
      <div
        style={{
          display: "inline-block",
          margin: "8px 0 16px",
          background: card.commBg,
          borderRadius: 999,
          padding: "6px 14px",
          fontWeight: 800,
          fontSize: 14,
          color: card.commFg,
          alignSelf: "flex-start",
          maxWidth: "100%",
          lineHeight: 1.35,
        }}
      >
        {card.commLabel}
      </div>
      <ul
        style={{
          listStyle: "none",
          margin: "4px 0 18px",
          padding: 0,
          // Per spec: `.ent ul { flex: 0 0 auto }` — the enterprise ul
          // does NOT expand; instead the dogmark wrapper below takes
          // `flex: 1` and centers the image in the remaining space.
          flex: card.image ? "0 0 auto" : 1,
        }}
      >
        {card.perks.map((p, i) => (
          <li
            key={i}
            style={{
              fontSize: 13.5,
              padding: "7px 0 7px 26px",
              position: "relative",
              borderBottom: `1px dashed ${isDark ? "rgba(255,255,255,.14)" : T.dashed}`,
              color: p.dim ? T.purple : textFg,
              opacity: p.dim ? 0.65 : 1,
              textDecoration: p.dim ? "line-through" : "none",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                fontSize: 12,
                color: p.dim ? T.purple : textFg,
                opacity: p.dim ? 0.65 : 1,
                // Per spec: `.ent li:before { filter: brightness(0) invert(1) }`
                // — on the dark enterprise card the paw emoji is inverted
                // to white so it reads against the charcoal background.
                filter: !p.dim && isDark ? "brightness(0) invert(1)" : "none",
              }}
              aria-hidden
            >
              {p.dim ? "—" : "🐾"}
            </span>
            {p.text}
          </li>
        ))}
      </ul>
      {card.image && (
        <div style={card.image.wrapperStyle} data-testid={`tier-image-${card.id}`}>
          <img
            src={card.image.src}
            alt={card.image.alt}
            style={card.image.imgStyle}
            data-testid={`tier-dogmark-${card.id}`}
          />
        </div>
      )}
      <div
        style={{
          background: "transparent",
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,.18)" : T.dashed}`,
          padding: "12px 2px 0",
          fontSize: 11.5,
          lineHeight: 1.5,
          marginBottom: 14,
          color: isDark ? T.mutedDk : T.muted,
        }}
      >
        {card.gate}
      </div>
      <button
        type="button"
        onClick={onCta}
        disabled={isLoading}
        data-testid={card.ctaTestId}
        style={{
          display: "block",
          textAlign: "center",
          fontWeight: 800,
          padding: 12,
          borderRadius: 11,
          fontSize: 14,
          border: "none",
          cursor: isLoading ? "wait" : "pointer",
          opacity: isLoading ? 0.7 : 1,
          ...card.ctaStyle,
        }}
      >
        {isLoading ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Loader2 size={14} style={{ animation: "pp-tier-spin 1s linear infinite" }} />
            {card.ctaLabel}
          </span>
        ) : (
          card.ctaLabel
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PartnerTiersPanel — the shared marketing surface. Parent decides
// what chrome to wrap it in (full public page shell OR pricing-tab
// insert). Parent also decides how CTAs are handled (auth flow, API
// POST, toast, navigation).
// ---------------------------------------------------------------------------
export default function PartnerTiersPanel({ onTierCta, busyTierId, showPayoutTable = false }) {
  return (
    <div data-testid="partner-tiers-panel">
      <div
        data-testid="tier-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 18,
          alignItems: "stretch",
        }}
        className="pp-tiers-grid"
      >
        {TIER_CARDS.map((card) => (
          <TierCard
            key={card.id}
            card={card}
            onCta={() => onTierCta && onTierCta(card.id)}
            isLoading={busyTierId === card.id}
          />
        ))}
      </div>

      {/* -----------------------------------------------------------------
          Level Up As You Grow — 3-bubble growth flow.
      ----------------------------------------------------------------- */}
      <section
        data-testid="level-up-section"
        style={{
          marginTop: 44,
          background: "#fff",
          border: `1px solid ${T.dashed}`,
          borderRadius: 16,
          padding: "0 26px 26px",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            fontSize: 17,
            margin: "0 -26px 22px",
            background: T.purple,
            color: "#fff",
            padding: "16px 26px",
            letterSpacing: ".3px",
          }}
        >
          Level Up As You Grow
        </h2>
        <div
          style={{
            display: "flex",
            gap: 0,
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="pp-level-steps"
        >
          {LEVEL_STEPS.map((step, i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "0 14px",
                  minWidth: 0,
                }}
                data-testid={`level-step-${i + 1}`}
              >
                <div
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: step.bubbleBg,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                    border: `3px solid ${step.borderColor}`,
                    lineHeight: 1.05,
                    boxShadow: step.ring || "none",
                  }}
                >
                  <span
                    style={{
                      color: step.bubbleFg,
                      fontWeight: 800,
                      fontSize: 26,
                      letterSpacing: "-.5px",
                    }}
                  >
                    {step.num}
                  </span>
                  <span
                    style={{
                      color: i === 2 ? "#FFD9A3" : (i === 1 ? "rgba(255,255,255,.85)" : T.muted),
                      fontWeight: 700,
                      fontSize: 10.5,
                      textTransform: "uppercase",
                      letterSpacing: ".8px",
                      marginTop: 3,
                    }}
                  >
                    {step.unit}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: step.slabColor,
                  }}
                >
                  {step.slab}
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: T.muted,
                    marginTop: 4,
                    lineHeight: 1.45,
                    maxWidth: 240,
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  {step.ssub}
                </div>
              </div>
              {i < LEVEL_STEPS.length - 1 && (
                <div
                  style={{
                    fontSize: 26,
                    color: T.orange,
                    fontWeight: 800,
                    flex: "0 0 40px",
                    textAlign: "center",
                  }}
                  aria-hidden
                  className="pp-level-arrow"
                >
                  →
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <p
          style={{
            marginTop: 26,
            fontSize: 12.5,
            color: T.muted,
            lineHeight: 1.6,
          }}
          data-testid="level-up-note"
        >
          Grow at your own pace — tier changes always come with notice, never surprises.
        </p>
      </section>

      {/* -----------------------------------------------------------------
          Payout table — LOCKED header per Feb 2026 amendment.
          HIDDEN by default (public marketing surfaces). Only renders
          when `showPayoutTable={true}` is passed by an authenticated
          consumer (e.g. future partner signup fee-disclosure step).
          Exposure would reveal derivable margin per tier (customer-pays
          vs partner-keeps) — sensitive info that should only appear
          after tier commitment.
      ----------------------------------------------------------------- */}
      {showPayoutTable && (
      <section
        data-testid="payout-table-section"
        style={{
          marginTop: 44,
          background: "#fff",
          border: `1px solid ${T.dashed}`,
          borderRadius: 16,
          padding: "0 26px 26px",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            fontSize: 17,
            margin: "0 -26px 16px",
            background: T.purple,
            color: "#fff",
            padding: "16px 26px",
            letterSpacing: ".3px",
          }}
          data-testid="payout-table-header"
        >
          {PAYOUT_TABLE_HEADER}
        </h2>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13.5,
            }}
            data-testid="payout-table"
          >
            <thead>
              <tr>
                {["Job", "Customer pays", "Standard pays you", "Preferred pays you", "Elite pays you", "Solo rate"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderBottom: `2px solid ${T.purple}`,
                      fontSize: 11.5,
                      textTransform: "uppercase",
                      letterSpacing: ".6px",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PAYOUT_ROWS.map((row) => (
                <tr key={row.job}>
                  <td style={{ padding: "9px 10px", borderBottom: `1px solid ${T.grey}` }}>{row.job}</td>
                  <td style={{ padding: "9px 10px", borderBottom: `1px solid ${T.grey}` }}>{row.cust}</td>
                  {[row.standard, row.preferred, row.elite].map((cell, idx) => (
                    <td
                      key={idx}
                      style={{
                        padding: "9px 10px",
                        borderBottom: `1px solid ${T.grey}`,
                        color: T.char,
                        fontWeight: 800,
                      }}
                    >
                      {cell[0]} <b style={{ color: T.orange }}>({cell[1]})</b>
                    </td>
                  ))}
                  <td style={{ padding: "9px 10px", borderBottom: `1px solid ${T.grey}` }}>{row.solo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* -----------------------------------------------------------------
          Deliberate absence — the tier surface must NOT surface either
          the trailing "cell-wins" clause OR the closing
          "every-tier scenario beats solo" line. Both are locked
          by copyAudit tests.
      ----------------------------------------------------------------- */}

      <style>{`
        @keyframes pp-tier-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @media (max-width: 980px) {
          .pp-tiers-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .pp-level-steps { flex-direction: column !important; gap: 14px !important; }
          .pp-level-arrow { transform: rotate(90deg); }
        }
        @media (max-width: 640px) {
          .pp-tiers-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// Re-export the locked header string so external tests / consumers
// (e.g. `Pricing.partnerTab.copyAudit.test.jsx`) can assert against
// the SAME constant that renders on the DOM. Prevents drift.
export { PAYOUT_TABLE_HEADER };
